import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { TextField, InputAdornment, IconButton, Paper, Box, Badge, Popover } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import FilterListIcon from '@mui/icons-material/FilterList';
import DeficiencyFilterMenu from '../../global/Components/DeficiencyFilterMenu';
import { canSeeCompanyFilter, canSeeInternalOnlyFilter } from '../../../utils/roles';
import { getDisplayLabelForInternal } from '../../../config/statusDisplayResolver';

/**
 * Component for searching and filtering pins on the floor plan.
 * Hides completed deficiencies by default (toggle to show).
 *
 * @param {Object} props
 * @param {Array} props.pins - The array of pins to search through
 * @param {Function} props.onSearchResults - Callback with filtered pins
 * @param {Function} props.onSelectPin - Callback when a single pin is found/selected
 * @param {string} props.currentUserRole - Current user's project role
 */
function PinSearch({ pins, onSearchResults, onSelectPin, currentUserRole }) {
    const [searchTerm, setSearchTerm] = useState('');
    const [filterAnchorEl, setFilterAnchorEl] = useState(null);
    const [filters, setFilters] = useState({
        statusFilters: [],
        companyFilters: [],
        pinColorFilters: [],
        showCompleted: false,
        showRejected: false,
        internalOnly: false,
    });

    // Track if changes came from search or filters for auto-select behavior
    const [isFilterChange, setIsFilterChange] = useState(false);
    const showCompanyFilter = canSeeCompanyFilter(currentUserRole);
    const showInternalOnlyFilter = canSeeInternalOnlyFilter(currentUserRole);

    // Filter pins whenever the search term, filters, or pins list changes
    useEffect(() => {
        let filteredPins = [...pins];

        // Hide completed by default unless toggled on or explicitly selected in status filter
        if (!filters.showCompleted && !filters.statusFilters.includes('Completed')) {
            filteredPins = filteredPins.filter((pin) => pin.status !== 'Completed');
        }

        // Hide rejected by default unless toggled on or explicitly selected in status filter
        if (!filters.showRejected && !filters.statusFilters.includes('Rejected')) {
            filteredPins = filteredPins.filter((pin) => pin.status !== 'Rejected');
        }

        // Internal only filter
        if (showInternalOnlyFilter && filters.internalOnly) {
            filteredPins = filteredPins.filter((pin) => pin.internalOnly === true);
        }

        // Apply text search
        if (searchTerm.trim()) {
            const lowercaseSearchTerm = searchTerm.toLowerCase();
            filteredPins = filteredPins.filter(
                (pin) =>
                    (pin.title && pin.title.toLowerCase().includes(lowercaseSearchTerm)) ||
                    (pin.description && pin.description.toLowerCase().includes(lowercaseSearchTerm))
            );
        }

        // Apply status filters
        if (filters.statusFilters.length > 0) {
            filteredPins = filteredPins.filter((pin) => {
                if (filters.statusFilters.includes(pin.status)) return true;
                if (currentUserRole) {
                    const displayLabel = getDisplayLabelForInternal(pin.status, currentUserRole);
                    return filters.statusFilters.includes(displayLabel);
                }
                return false;
            });
        }

        // Apply company filters
        if (showCompanyFilter && filters.companyFilters.length > 0) {
            filteredPins = filteredPins.filter((pin) =>
                filters.companyFilters.some((c) => c.companyId === pin.assignedToBusinessId)
            );
        }

        // Apply pin color filters
        if (filters.pinColorFilters.length > 0) {
            filteredPins = filteredPins.filter((pin) =>
                filters.pinColorFilters.includes(pin.pinColor)
            );
        }

        onSearchResults(filteredPins);

        // Auto-select pin only for text search with exactly one result
        if (filteredPins.length === 1 && !isFilterChange && onSelectPin && searchTerm.trim()) {
            onSelectPin(filteredPins[0]);
        }

        setIsFilterChange(false);
    }, [
        searchTerm,
        filters,
        pins,
        onSearchResults,
        onSelectPin,
        isFilterChange,
        currentUserRole,
        showCompanyFilter,
        showInternalOnlyFilter,
    ]);

    const handleClearSearch = () => {
        setSearchTerm('');
    };

    const handleOpenFilter = (event) => {
        setFilterAnchorEl(event.currentTarget);
    };

    const handleCloseFilter = () => {
        setFilterAnchorEl(null);
    };

    const toggleArrayFilter = useCallback((filterKey, value) => {
        setIsFilterChange(true);
        setFilters((prev) => {
            const current = [...prev[filterKey]];
            const index = current.indexOf(value);
            if (index === -1) {
                current.push(value);
            } else {
                current.splice(index, 1);
            }
            return { ...prev, [filterKey]: current };
        });
    }, []);

    const toggleCompanyFilter = useCallback((company) => {
        setIsFilterChange(true);
        setFilters((prev) => {
            const current = [...prev.companyFilters];
            const index = current.findIndex((c) => c.companyId === company.companyId);
            if (index === -1) {
                current.push(company);
            } else {
                current.splice(index, 1);
            }
            return { ...prev, companyFilters: current };
        });
    }, []);

    const toggleBooleanFilter = useCallback((filterKey) => {
        setIsFilterChange(true);
        setFilters((prev) => ({ ...prev, [filterKey]: !prev[filterKey] }));
    }, []);

    const activeFilterCount =
        filters.statusFilters.length +
        (showCompanyFilter ? filters.companyFilters.length : 0) +
        filters.pinColorFilters.length +
        (filters.showCompleted ? 1 : 0) +
        (filters.showRejected ? 1 : 0) +
        (showInternalOnlyFilter && filters.internalOnly ? 1 : 0);

    const filterOpen = Boolean(filterAnchorEl);

    return (
        <Box>
            <Paper
                elevation={0}
                sx={{
                    p: 0.5,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                }}
            >
                <TextField
                    size="small"
                    placeholder="Search deficiencies..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{ flexGrow: 1 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" color="action" />
                            </InputAdornment>
                        ),
                        endAdornment: searchTerm && (
                            <InputAdornment position="end">
                                <IconButton
                                    edge="end"
                                    onClick={handleClearSearch}
                                    size="small"
                                    aria-label="clear search"
                                >
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            </InputAdornment>
                        ),
                        sx: { borderRadius: 2 },
                    }}
                />

                <Badge
                    badgeContent={activeFilterCount}
                    color="primary"
                    invisible={activeFilterCount === 0}
                    sx={{
                        '& .MuiBadge-badge': {
                            fontSize: '0.7rem',
                            height: 18,
                            minWidth: 18,
                        },
                    }}
                >
                    <IconButton
                        onClick={handleOpenFilter}
                        color={filterOpen ? 'primary' : 'default'}
                        aria-label="filter deficiencies"
                        sx={{
                            padding: { xs: '4px', sm: '8px' },
                            marginLeft: { xs: '-4px', sm: 0 },
                        }}
                    >
                        <FilterListIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </Badge>
            </Paper>

            <Popover
                open={filterOpen}
                anchorEl={filterAnchorEl}
                onClose={handleCloseFilter}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                PaperProps={{
                    sx: {
                        width: 260,
                        maxHeight: { xs: '70vh', md: 480 },
                        overflow: 'auto',
                        borderRadius: 2,
                        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                        py: 1,
                    },
                }}
                marginThreshold={8}
            >
                <DeficiencyFilterMenu
                    deficiencies={pins}
                    sections={{
                        status: true,
                        company: showCompanyFilter,
                        pinColor: true,
                        showCompleted: true,
                        showRejected: true,
                        internalOnly: showInternalOnlyFilter,
                    }}
                    filters={filters}
                    onToggle={{
                        status: (status) => toggleArrayFilter('statusFilters', status),
                        company: toggleCompanyFilter,
                        pinColor: (color) => toggleArrayFilter('pinColorFilters', color),
                        showCompleted: () => toggleBooleanFilter('showCompleted'),
                        showRejected: () => toggleBooleanFilter('showRejected'),
                        internalOnly: () => toggleBooleanFilter('internalOnly'),
                    }}
                    role={currentUserRole}
                />
            </Popover>
        </Box>
    );
}

PinSearch.propTypes = {
    pins: PropTypes.array.isRequired,
    onSearchResults: PropTypes.func.isRequired,
    onSelectPin: PropTypes.func.isRequired,
    currentUserRole: PropTypes.string,
};

export default PinSearch;
